/* eslint-disable no-use-before-define */
import { take } from '@redux-saga/core/effects'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiTransferOfUnits } from 'api/_legacy/generated-api'
import {
  ApiAccountBuilderState,
  ApiAccountState,
  ApiAssetPriceWithHistory,
  ApiAssetWeight,
  ApiBacktestResponse,
  ApiCalculatedPortfolioMultiplier,
  ApiFundOfFundsPortfolio,
  ApiModelPortfolio,
  ApiModelPortfolioMinimal,
  ApiMutualFundAssetRead,
  ApiNote,
  ApiNoteData,
  ApiPortfolioInvestment,
  ApiPortfolioType,
  ApiProjectionResponse,
  ApiRebalancingDefinition,
  ApiRebalancingFrequency,
  ApiRebalancingType,
  ApiSegregatedPortfolioAssetRead,
  ApiSelfSelectPortfolio,
  ApiStrategyTemplate,
  ApiTaxClassification,
  ApiUnapprovedAccount,
  Factor
} from 'api/rtk-api'
import { addYears } from 'date-fns'
import { all, call, debounce, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects'
import type { RootState } from 'state/store'

import { createLogoutExtraReducers } from '@/utilities/redux'

import api from '../../../../../api/_legacy'
import { TopUpWithdrawState } from '../../../../trade-editor/types/TopUpWithdrawState'
import { Fee, SavedState } from '../../../types'
import {
  equalWeights,
  formatFees,
  getRecordFromFactor,
  round,
  scaleWeights,
  truncate
} from '../../../util'
import { getDateOnlyAsJson } from '../../../util/get-date-only-as-json'
import { Errors } from '../../../util/validator'
import {
  getDisallowedStrategiesFromCurrentState,
  getDisallowedStrategiesFromPendingState,
  getMergedDisallowedStrategies
} from '../../00-builder/state/mappers'
import { FundingSource } from '../../02-terms/state/terms-slice'
import { extractAssetPerformance } from '../mappers/extract-asset-performance'
import { AssetSummaryItem, ModelPortfolio, SelfSelectPortfolio } from '../types'
import { validate } from './validation-rules'

const TENANT_ID = 'absa'

type PortfolioResult = {
  id: string
  name: string
  portfolioType?: ApiPortfolioType
  taxClassification: string
}

export type AssetResult = {
  assetClass: string
  assetType: string
  id: string
  isMarketDataReady: boolean
  name: string
  protectionType: string
  taxClassification: string
}

type AvailableStrategy = {
  isAvailable: boolean
  name: string
}

export interface ReadOnlyPortfolioState {
  disallowedStrategies?: string[] | null
  investmentWeights?: ApiPortfolioInvestment[]
  multiplier?: number | null
  portfolioId: string
  portfolioName?: string | null
  portfolioType: ApiPortfolioType
  rebalDef?: ApiRebalancingDefinition
  targetWeights?: ApiAssetWeight[]
  taxClassification?: ApiTaxClassification
}

export type ModelPortfolioState = {
  allAssetPerformance?: Record<string, AssetSummaryItem[]>
  assets?: AssetSummaryItem[]
  availableStrategies?: AvailableStrategy[]
  calculatedMultiplier?: number
  disallowedStrategies: string[]
  fees?: Fee[]
  isChoosing: boolean
  isChosen: boolean
  isInvalidTaxClassification: boolean
  isTaxClassificationWarning: boolean
  performanceMetrics?: Record<string, number>
  portfolio?: ModelPortfolio
  projection?: Record<string, number>[]
  selectedAssetDuration?: string
}

export type SelfSelectPortfolioState = {
  allAssetPerformance?: Record<string, AssetSummaryItem[]>
  areWeightsValid: boolean
  assets?: AssetSummaryItem[]
  availableStrategies?: AvailableStrategy[]
  basePortfolio?: ModelPortfolio
  calculatedMultiplier?: number
  disallowedStrategies: string[]
  errors: Errors
  isChoosingAsset: boolean
  isChoosingBasePortfolio: boolean
  isChosenAsset: boolean
  isInvalidTaxClassification: boolean
  isTaxClassificationWarning: boolean
  performanceMetrics?: Record<string, number>
  portfolio?: SelfSelectPortfolio
  projection?: Record<string, number>[]
  selectedAssetDuration?: string
  selfSelectType?: 'new' | 'modify' | 'none'
  taxClassification?: ApiTaxClassification
  totalPortfolioValue?: number
  totalWeights?: number
  transferUnits?: ApiTransferOfUnits[]
}

export type SearchPortfolioState = {
  allAssetPerformance?: Record<string, AssetSummaryItem[]>
  filteredPortfolios: PortfolioResult[]
  portfolioBeingInspected?: ModelPortfolio
  portfolios: PortfolioResult[]
  searchTerm: string
  status: 'finding' | 'inspecting'
}

export type SearchAssetState = {
  assetBeingInspected?: ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead
  assets: AssetResult[]
  filteredAssets: AssetResult[]
  searchTerm: string
  status: 'finding' | 'inspecting'
}

export type PortfolioStepState = {
  assetBeingInspected?: ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead
  assetPrices: Record<string, number>
  builderErrors: Errors
  canProceed: boolean
  dataErrorText: string
  errors: Errors
  isDataError: boolean
  isErrorGeneratingBacktest: boolean
  isErrorGeneratingProjection: boolean
  isGeneratingBacktest: boolean
  isGeneratingProjection: boolean
  isInspectingAsset: boolean
  isTransfer: boolean
  model?: ModelPortfolioState & {
    portfolio?: {
      startDate?: string
    }
  }
  note?: ApiNote
  portfolioType?: ApiPortfolioType
  readonlyCurrentPortfolio?: ReadOnlyPortfolioState
  readonlyPendingPortfolio?: ReadOnlyPortfolioState
  search: SearchPortfolioState
  searchAsset: SearchAssetState
  selfSelect?: SelfSelectPortfolioState
  strategies?: ApiStrategyTemplate[]
}

const initialState: PortfolioStepState = {
  assetPrices: {},
  canProceed: false,
  isDataError: false,
  dataErrorText: '',
  errors: {},
  builderErrors: {},
  search: {
    status: 'finding',
    searchTerm: '',
    portfolios: [],
    filteredPortfolios: []
  },
  searchAsset: {
    status: 'finding',
    searchTerm: '',
    assets: [],
    filteredAssets: []
  },
  isInspectingAsset: false,
  isGeneratingBacktest: false,
  isGeneratingProjection: false,
  isErrorGeneratingBacktest: false,
  isErrorGeneratingProjection: false,
  isTransfer: false
}

function getTotalPortfolioValueFromUnits(
  assets: AssetSummaryItem[],
  prices: Record<string, number>
) {
  const totalPortfolioValue = assets
    .map(asset => asset.units * prices[asset.id])
    .reduce((a, b) => a + b)
  return totalPortfolioValue
}

export const portfolioSlice = createSlice({
  name: 'portfolioStep',
  initialState,
  reducers: {
    reset: state => {
      state.canProceed = false
    },
    resetPortfolio: state => {
      state.readonlyCurrentPortfolio = initialState.readonlyCurrentPortfolio
      state.readonlyPendingPortfolio = initialState.readonlyPendingPortfolio
      state.model = initialState.model
      state.selfSelect = initialState.selfSelect
      state.portfolioType = initialState.portfolioType
    },
    init: (state, action: PayloadAction<ApiNote>) => {
      const note = action.payload
      if (!note) {
        state.isDataError = true
        state.dataErrorText = 'A note must be provided during initialisation'
      } else {
        state.isDataError = false
        state.dataErrorText = ''
        state.note = note

        if (state.model?.isChosen) {
          if (state.note.taxClassification) {
            const isInvalid = note.taxClassification !== state.model.portfolio.taxClassification
            state.model.isTaxClassificationWarning = isInvalid
          }
        }
      }
    },
    setReadonlyCurrentOrPending: (
      state,
      action: PayloadAction<ApiAccountState | ApiAccountBuilderState>
    ) => {
      if (action.payload) {
        const { portfolio } = action.payload
        if (portfolio) {
          const isPending = (action.payload as ApiAccountBuilderState).accountName === undefined
          const modelPortfolio = portfolio as ApiModelPortfolio
          const foFPortfolio = portfolio as ApiFundOfFundsPortfolio
          const selfSelectPortfolio = portfolio as ApiSelfSelectPortfolio
          const isModel = portfolio.type === 'ModelPortfolio'
          const isFof = portfolio.type === 'FundOfFunds'

          const portfolioId = isModel ? (modelPortfolio as any).portfolioId : 'SelfSelect'
          const portfolioName = isModel ? (modelPortfolio as any).portfolioName : ''
          const targetWeights = isModel
            ? modelPortfolio?.targetWeights
            : selfSelectPortfolio?.targetWeights
          const investmentWeights = !!isFof && foFPortfolio?.investmentWeights
          const rebalDef = isModel
            ? modelPortfolio?.rebalancingDefinition
            : selfSelectPortfolio?.rebalancingDefinition
          const multiplier = isModel ? modelPortfolio.multiplier : undefined
          const taxClassification = isModel ? modelPortfolio.taxClassification : undefined

          const currentOrPending: ReadOnlyPortfolioState = {
            portfolioId,
            portfolioType: portfolio.type,
            portfolioName,
            targetWeights,
            investmentWeights,
            rebalDef,
            multiplier,
            taxClassification
          }

          if (isPending) {
            state.readonlyPendingPortfolio = currentOrPending
          } else {
            state.readonlyCurrentPortfolio = currentOrPending
          }
        }
      }
    },
    setPortfolioTypeModel: state => {
      state.portfolioType = 'ModelPortfolio'
      state.model = {
        isChosen: false,
        isChoosing: false,
        isTaxClassificationWarning: false,
        isInvalidTaxClassification: false,
        disallowedStrategies: []
      }
      state.canProceed = false
    },
    setPortfolioTypeFundOfFund: state => {
      state.portfolioType = 'FundOfFunds'
      state.model = {
        isChosen: false,
        isChoosing: false,
        isTaxClassificationWarning: false,
        isInvalidTaxClassification: false,
        disallowedStrategies: []
      }
      state.canProceed = false
    },
    setPortfolioTypeSelfSelect: state => {
      state.portfolioType = 'SelfSelect'
      state.selfSelect = {
        areWeightsValid: false,
        isChosenAsset: false,
        isChoosingAsset: false,
        isChoosingBasePortfolio: false,
        disallowedStrategies: [],
        errors: {},
        totalWeights: 0,
        isTaxClassificationWarning: false,
        isInvalidTaxClassification: false,
        assets: [],
        portfolio: {
          rebalancingDefinition: {
            type: 'None',
            frequency: null,
            weightsTolerance: null
          },
          targetWeights: [],
          type: 'SelfSelect'
        },
        transferUnits: []
      }
      state.canProceed = false
    },
    setSelfSelectType: (state, action: PayloadAction<string>) => {
      state.selfSelect.selfSelectType = action.payload as 'new' | 'modify' | 'none'
    },
    setSelfSelectRebalancingType: (state, action: PayloadAction<ApiRebalancingType>) => {
      state.selfSelect.portfolio.rebalancingDefinition.type = action.payload
    },
    setSelfSelectRebalancingFreq: (state, action: PayloadAction<ApiRebalancingFrequency>) => {
      state.selfSelect.portfolio.rebalancingDefinition.frequency = action.payload
    },
    setSelfSelectRebalancingTolerance: (state, action: PayloadAction<number>) => {
      state.selfSelect.portfolio.rebalancingDefinition.weightsTolerance = action.payload
    },
    chooseModelPortfolio: state => {
      if (state.portfolioType === 'ModelPortfolio' || state.portfolioType === 'FundOfFunds') {
        state.model.isChoosing = true
      }
      if (state.portfolioType === 'SelfSelect') {
        state.selfSelect.isChoosingBasePortfolio = true
      }
    },
    chooseAsset: state => {
      state.selfSelect.isChoosingAsset = true
    },
    fetchPortfoliosSuccess: (state, action: PayloadAction<ApiModelPortfolioMinimal[]>) => {
      state.search.portfolios = action.payload.map(x => ({
        id: x.portfolioId,
        name: x.portfolioName,
        portfolioType: x.portfolioType,
        taxClassification: x.taxClassification
      }))
      state.search.filteredPortfolios = [...state.search.portfolios]
    },
    fetchPortfolioDetailSuccess: (state, action: PayloadAction<ApiModelPortfolio>) => {
      state.search.portfolioBeingInspected = action.payload as any
    },
    fetchNoteDataSuccess: (state, action: PayloadAction<ApiNoteData>) => {
      if (state.note.taxClassification) {
        if (state.portfolioType === 'ModelPortfolio' || state.portfolioType === 'FundOfFunds') {
          state.model.isInvalidTaxClassification =
            state.note.taxClassification !== action.payload.newCalculatedNoteTaxClassifications
          if (state.model.isInvalidTaxClassification) {
            state.canProceed = false
          } else {
            state.canProceed = true
          }
        }
        if (state.portfolioType === 'SelfSelect') {
          state.selfSelect.isInvalidTaxClassification =
            state.note.taxClassification !== action.payload.newCalculatedNoteTaxClassifications
          if (state.selfSelect.isInvalidTaxClassification || !state.selfSelect.areWeightsValid) {
            state.canProceed = false
          } else {
            state.canProceed = true
          }
        }
      } else if (
        state.portfolioType === 'ModelPortfolio' ||
        state.portfolioType === 'FundOfFunds' ||
        state?.selfSelect?.areWeightsValid
      ) {
        state.canProceed = true
      } else {
        state.canProceed = false
      }
    },
    fetchAssetPerformancesSuccess: (state, action: PayloadAction<ApiBacktestResponse>) => {
      state.search.allAssetPerformance = extractAssetPerformance(
        state.search.portfolioBeingInspected.targetWeights,
        action.payload.assetPerformance
      )
    },
    fetchStrategiesSuccess: (state, action: PayloadAction<ApiStrategyTemplate[]>) => {
      state.strategies = action.payload
    },
    fetchAssetsSuccess: (
      state,
      action: PayloadAction<(ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead)[]>
    ) => {
      const allAssets = action.payload.map(x => ({
        id: x.assetId,
        name: x.name,
        assetType: x.type,
        protectionType: x.protectionClassification,
        taxClassification: x.taxClassification,
        assetClass: x.assetClass,
        isMarketDataReady: x.isMarketDataReady
      }))
      const assetIdsInPortfolio = state.selfSelect.assets.map(x => x.id)
      state.searchAsset.assets = allAssets
        .filter(x => !assetIdsInPortfolio.includes(x.id))
        .filter(x => x.protectionType !== 'Protection')
      state.searchAsset.filteredAssets = [...state.searchAsset.assets]
    },
    fetchAssetDetailSuccess: (
      state,
      action: PayloadAction<ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead>
    ) => {
      state.searchAsset.assetBeingInspected = action.payload
    },
    fetchAssetPricesSuccess: (state, action: PayloadAction<ApiAssetPriceWithHistory[]>) => {
      action.payload.forEach(assetPriceEntry => {
        if (assetPriceEntry.currentPrice) {
          state.assetPrices[assetPriceEntry.isinCode] = assetPriceEntry.currentPrice.price / 100 // TODO - get price scaling factor from server
        }
      })
      if (state.selfSelect) {
        state.selfSelect.totalPortfolioValue =
          state.selfSelect.assets.length > 0
            ? getTotalPortfolioValueFromUnits(state.selfSelect.assets, state.assetPrices)
            : 0
      }
    },
    fetchingPortfolioBacktest: state => {
      state.isErrorGeneratingBacktest = false
      state.isGeneratingBacktest = true
    },
    fetchingPortfolioProjection: state => {
      state.isErrorGeneratingProjection = false
      state.isGeneratingProjection = true
    },
    fetchPortfolioBacktestSuccess: (state, action: PayloadAction<ApiBacktestResponse>) => {
      switch (state.portfolioType) {
        case 'ModelPortfolio':
          state.model.performanceMetrics = getRecordFromFactor(action.payload.backtestTimeseries)

          state.model.allAssetPerformance = extractAssetPerformance(
            state.model.portfolio?.targetWeights,
            action.payload.assetPerformance
          )
          state.model.selectedAssetDuration = '6m'
          state.model.assets = state.model.allAssetPerformance[state.model.selectedAssetDuration]
          break
        case 'FundOfFunds':
          state.model.performanceMetrics = getRecordFromFactor(action.payload.backtestTimeseries)

          state.model.allAssetPerformance = extractAssetPerformance(
            state.model.portfolio?.targetWeights,
            action.payload.assetPerformance
          )
          state.model.selectedAssetDuration = '6m'
          state.model.assets = state.model.allAssetPerformance[state.model.selectedAssetDuration]
          break
        case 'SelfSelect':
          state.selfSelect.performanceMetrics = getRecordFromFactor(
            action.payload.backtestTimeseries
          )
          state.selfSelect.allAssetPerformance = extractAssetPerformance(
            state.selfSelect.portfolio?.targetWeights,
            action.payload.assetPerformance
          )
          state.selfSelect.selectedAssetDuration = '6m'
          state.selfSelect.assets.forEach(asset => {
            asset.return = state.selfSelect.allAssetPerformance[
              state.selfSelect.selectedAssetDuration
            ].find(x => x.id === asset.id).return
            asset.volatility = state.selfSelect.allAssetPerformance[
              state.selfSelect.selectedAssetDuration
            ].find(x => x.id === asset.id).volatility
          })
      }
      state.isGeneratingBacktest = false
    },
    fetchPortfolioProjectionSuccess: (state, action: PayloadAction<ApiProjectionResponse>) => {
      switch (state.portfolioType) {
        case 'ModelPortfolio':
          state.model.projection = Object.values(action.payload).filter(x => x) as any
          break
        case 'FundOfFunds':
          state.model.projection = Object.values(action.payload).filter(x => x) as any
          break
        case 'SelfSelect':
          state.selfSelect.projection = Object.values(action.payload).filter(x => x) as any
      }
      state.isGeneratingProjection = false
    },
    fetchCalculatedMultiplierSuccess: (
      state,
      action: PayloadAction<ApiCalculatedPortfolioMultiplier>
    ) => {
      switch (state.portfolioType) {
        case 'ModelPortfolio':
          state.model.calculatedMultiplier = action.payload.multiplier
          break
        case 'FundOfFunds':
          state.model.calculatedMultiplier = action.payload.multiplier
          break
        case 'SelfSelect':
          state.selfSelect.calculatedMultiplier = action.payload.multiplier
      }
    },
    fetchPortfolioBacktestFailure: state => {
      state.isGeneratingBacktest = false
      state.isErrorGeneratingBacktest = true
    },
    fetchPortfolioProjectionFailure: state => {
      state.isGeneratingProjection = false
      state.isErrorGeneratingProjection = true
    },
    setModelPortfolio: (
      state,
      action: PayloadAction<{ disallowedStrategiesFromState: string[]; model: ApiModelPortfolio }>
    ) => {
      const modelPortfolio = action.payload.model
      const { disallowedStrategiesFromState } = action.payload
      state.model.isChosen = true
      state.model.isChoosing = false
      state.search.status = 'finding'
      state.model.portfolio = modelPortfolio as any

      // all strategies
      // see what is disabled in this

      const availableStrategies = state.strategies.map(x => ({
        name: x.strategyName,
        isAvailable: !disallowedStrategiesFromState.includes(x.strategyTemplateId)
      }))

      state.model.availableStrategies = availableStrategies
      state.model.disallowedStrategies = disallowedStrategiesFromState
      state.model.fees = formatFees(action.payload.model.fees)

      if (state.note.taxClassification) {
        state.model.isTaxClassificationWarning =
          modelPortfolio.taxClassification !== state.note.taxClassification
      }
    },
    setBasePortfolio: (
      state,
      action: PayloadAction<{ disallowedStrategiesFromState: string[]; model: ApiModelPortfolio }>
    ) => {
      const basePortfolio = action.payload.model

      state.selfSelect.isChosenAsset = true
      state.selfSelect.isChoosingBasePortfolio = false
      state.search.status = 'finding'
      state.selfSelect.basePortfolio = basePortfolio as any

      state.selfSelect.portfolio.targetWeights = basePortfolio.targetWeights
      state.selfSelect.portfolio.rebalancingDefinition = basePortfolio.rebalancingDefinition
      state.selfSelect.portfolio.type = 'SelfSelect'

      state.selfSelect.assets = []
      basePortfolio.targetWeights.forEach(x => {
        const asset = {
          id: x.asset.assetId,
          name: x.asset.name,
          return: null,
          units: null,
          volatility: null,
          weight: x.weight
        }
        state.selfSelect.assets.push(asset)
      })

      const yieldWeight = basePortfolio.targetWeights
        .filter(x => x.asset.taxClassification === 'Yield')
        .map(x => x.weight)
        .reduce((sum, x) => sum + x, 0)
      state.selfSelect.taxClassification = yieldWeight >= 0.5 ? 'Yield' : 'Capital'

      const availableStrategies = state.strategies.map(x => ({
        name: x.strategyName,
        isAvailable: !action.payload.disallowedStrategiesFromState.includes(x.strategyTemplateId)
      }))

      state.selfSelect.availableStrategies = availableStrategies
      state.selfSelect.totalWeights = 1

      if (state.note.taxClassification) {
        state.selfSelect.isTaxClassificationWarning =
          state.selfSelect.taxClassification !== state.note.taxClassification
      }
    },
    setSelfSelectPortfolio: (
      state,
      action: PayloadAction<{
        disallowedStrategiesFromState: string[]
        isTransfer: boolean
        portfolio: ApiSelfSelectPortfolio
        transferUnits: ApiTransferOfUnits[]
      }>
    ) => {
      const { portfolio } = action.payload
      const { disallowedStrategiesFromState } = action.payload
      state.selfSelect.isChosenAsset = true
      state.selfSelect.isChoosingAsset = false
      state.searchAsset.status = 'finding'
      state.selfSelect.selfSelectType = 'new'
      state.selfSelect.portfolio = portfolio

      portfolio.targetWeights.forEach(x => {
        const asset = {
          id: x.asset.assetId,
          name: x.asset.name,
          return: null,
          units: action.payload.transferUnits?.find(
            transfer => transfer.assetId === x.asset.assetId
          ).units,
          volatility: null,
          weight: x.weight
        }
        state.selfSelect.assets.push(asset)
      })

      const yieldWeight = portfolio.targetWeights
        .filter(x => x.asset.taxClassification === 'Yield')
        .map(x => x.weight)
        .reduce((sum, x) => sum + x, 0)
      state.selfSelect.taxClassification = yieldWeight >= 0.5 ? 'Yield' : 'Capital'

      let availableStrategies = []
      if (!action.payload.isTransfer) {
        availableStrategies = state.strategies.map(x => ({
          name: x.strategyName,
          isAvailable: !disallowedStrategiesFromState.includes(x.strategyTemplateId)
        }))
        state.selfSelect.disallowedStrategies = disallowedStrategiesFromState
      } else {
        availableStrategies = state.strategies.map(x => ({
          name: x.strategyName,
          isAvailable: x.strategyTemplateId === 'Delta1'
        }))
        state.selfSelect.disallowedStrategies = state.strategies
          .filter(strategy => strategy.strategyTemplateId !== 'Delta1')
          .map(strategy => strategy.strategyTemplateId)

        state.selfSelect.transferUnits = action.payload.transferUnits
        state.isTransfer = true
        state.selfSelect.totalPortfolioValue =
          state.selfSelect.assets.length > 0
            ? getTotalPortfolioValueFromUnits(state.selfSelect.assets, state.assetPrices)
            : 0
      }
      state.selfSelect.availableStrategies = availableStrategies
      state.selfSelect.totalWeights = 1
      state.selfSelect.areWeightsValid = true

      if (state.note.taxClassification) {
        state.selfSelect.isTaxClassificationWarning =
          state.selfSelect.taxClassification !== state.note.taxClassification
      }
    },
    setAssetInSelfSelect: (
      state,
      action: PayloadAction<{
        asset: ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead
        businessUnitDisallowedStrategies: string[]
        isTransfer: boolean
      }>
    ) => {
      const asset = {
        id: action.payload.asset.assetId,
        name: action.payload.asset.name,
        return: null,
        units: null,
        volatility: null,
        weight: null
      }
      state.selfSelect.assets.push(asset)
      const targetWeight = {
        asset: action.payload.asset,
        weight: null
      }
      state.selfSelect.portfolio.targetWeights.push(targetWeight)

      let availableStrategies = []
      if (!action.payload.isTransfer) {
        const disallowedStrategiesSet = new Set<string>()
        state.selfSelect.portfolio.targetWeights.map(x =>
          x.asset.disallowedStrategies.forEach(item => disallowedStrategiesSet.add(item))
        )
        const disallowedStrategies = getMergedDisallowedStrategies(
          action.payload.businessUnitDisallowedStrategies,
          Array.from(disallowedStrategiesSet)
        )

        availableStrategies = state.strategies.map(x => ({
          name: x.strategyName,
          isAvailable: !disallowedStrategies.includes(x.strategyTemplateId)
        }))
      } else {
        availableStrategies = state.strategies.map(x => ({
          name: x.strategyName,
          isAvailable: x.strategyTemplateId === 'Delta1'
        }))
        state.isTransfer = true
      }
      state.selfSelect.availableStrategies = availableStrategies
      state.selfSelect.isChosenAsset = true
      state.selfSelect.isChoosingAsset = false
      state.searchAsset.status = 'finding'
    },
    removeAssetFromSelfSelect: (
      state,
      action: PayloadAction<{
        businessUnitDisallowedStrategies: string[]
        id: string
        isTransfer: boolean
      }>
    ) => {
      state.selfSelect.assets = state.selfSelect.assets.filter(x => x.id !== action.payload.id)
      state.selfSelect.portfolio.targetWeights = state.selfSelect.portfolio.targetWeights.filter(
        x => x.asset.assetId !== action.payload.id
      )

      let availableStrategies = []
      if (!action.payload.isTransfer) {
        const disallowedStrategiesSet = new Set<string>()
        state.selfSelect.portfolio.targetWeights.map(x =>
          x.asset.disallowedStrategies.forEach(item => disallowedStrategiesSet.add(item))
        )
        const disallowedStrategies = getMergedDisallowedStrategies(
          action.payload.businessUnitDisallowedStrategies,
          Array.from(disallowedStrategiesSet)
        )

        availableStrategies = state.strategies.map(x => ({
          name: x.strategyName,
          isAvailable: !disallowedStrategies.includes(x.strategyTemplateId)
        }))

        delete state.selfSelect.errors['total-weights']
        const weights = state.selfSelect.portfolio.targetWeights.map(x => x.weight)
        state.selfSelect.totalWeights = weights.reduce((sum, current) => sum + current, 0)
        if (state.selfSelect.totalWeights !== 1) {
          state.selfSelect.errors['total-weights'] = 'Weights must sum to 100%'
          state.selfSelect.areWeightsValid = false
        } else {
          state.selfSelect.areWeightsValid = true
        }
      } else {
        availableStrategies = state.strategies.map(x => ({
          name: x.strategyName,
          isAvailable: x.strategyTemplateId === 'Delta1'
        }))

        state.selfSelect.transferUnits = state.selfSelect.transferUnits.filter(
          transfer => transfer.assetId !== action.payload.id
        )

        const totalPortfolioValue =
          state.selfSelect.assets.length > 0
            ? getTotalPortfolioValueFromUnits(state.selfSelect.assets, state.assetPrices)
            : 0
        state.selfSelect.totalPortfolioValue = totalPortfolioValue

        state.selfSelect.assets.forEach(asset => {
          const weight = (asset.units * state.assetPrices[asset.id]) / totalPortfolioValue
          asset.weight = weight
          const i = state.selfSelect.portfolio.targetWeights.findIndex(
            x => x.asset.assetId === asset.id
          )
          state.selfSelect.portfolio.targetWeights[i].weight = weight
        })

        delete state.selfSelect.errors['total-weights']
        state.selfSelect.totalWeights = 1
        state.selfSelect.areWeightsValid = true
      }
      state.selfSelect.availableStrategies = availableStrategies

      const yieldWeight = state.selfSelect.portfolio.targetWeights
        .filter(x => x.asset.taxClassification === 'Yield')
        .map(x => x.weight)
        .reduce((sum, x) => sum + x, 0)
      state.selfSelect.taxClassification =
        yieldWeight >= 0.5 * state.selfSelect.totalWeights ? 'Yield' : 'Capital'

      if (state.note.taxClassification) {
        state.selfSelect.isTaxClassificationWarning =
          state.selfSelect.taxClassification !== state.note.taxClassification
      }
      if (state.selfSelect.assets.length === 0) {
        state.selfSelect.isChosenAsset = false
      }
    },
    changeSelfSelectAssetWeight: (
      state,
      action: PayloadAction<{ assetId: string; weight: number }>
    ) => {
      const i = state.selfSelect.portfolio.targetWeights.findIndex(
        x => x.asset.assetId === action.payload.assetId
      )
      state.selfSelect.portfolio.targetWeights[i].weight = action.payload.weight
      const j = state.selfSelect.assets.findIndex(x => x.id === action.payload.assetId)
      state.selfSelect.assets[j].weight = action.payload.weight

      delete state.selfSelect.errors['total-weights']
      const weights = state.selfSelect.portfolio.targetWeights.map(x => x.weight)
      state.selfSelect.totalWeights = round(
        weights.reduce((sum, current) => sum + current, 0),
        8
      )
      if (state.selfSelect.totalWeights !== 1) {
        state.selfSelect.errors['total-weights'] = 'Weights must sum to 100%'
        state.selfSelect.areWeightsValid = false
      } else {
        state.selfSelect.areWeightsValid = true
      }

      const yieldWeight = state.selfSelect.portfolio.targetWeights
        .filter(x => x.asset.taxClassification === 'Yield')
        .map(x => x.weight)
        .reduce((sum, x) => sum + x, 0)
      state.selfSelect.taxClassification =
        yieldWeight >= 0.5 * state.selfSelect.totalWeights ? 'Yield' : 'Capital'

      if (state.note.taxClassification) {
        state.selfSelect.isTaxClassificationWarning =
          state.selfSelect.taxClassification !== state.note.taxClassification
      }
    },
    equaliseSelfSelectWeights: state => {
      const numAssets = state.selfSelect.portfolio.targetWeights.length
      const weightsArr = equalWeights(numAssets, 4)
      state.selfSelect.assets.map((x, i) => (x.weight = weightsArr[i]))
      state.selfSelect.portfolio.targetWeights.map((x, i) => (x.weight = weightsArr[i]))

      delete state.selfSelect.errors['total-weights']
      const weights = state.selfSelect.portfolio.targetWeights.map(x => x.weight)
      state.selfSelect.totalWeights = weights.reduce((sum, current) => sum + current, 0)
      if (state.selfSelect.totalWeights !== 1) {
        state.selfSelect.errors['total-weights'] = 'Weights must sum to 100%'
        state.selfSelect.areWeightsValid = false
      } else {
        state.selfSelect.areWeightsValid = true
      }

      const yieldWeight = state.selfSelect.portfolio.targetWeights
        .filter(x => x.asset.taxClassification === 'Yield')
        .map(x => x.weight)
        .reduce((sum, x) => sum + x, 0)
      state.selfSelect.taxClassification =
        yieldWeight >= 0.5 * state.selfSelect.totalWeights ? 'Yield' : 'Capital'

      if (state.note.taxClassification) {
        state.selfSelect.isTaxClassificationWarning =
          state.selfSelect.taxClassification !== state.note.taxClassification
      }
    },
    scaleSelfSelectWeights: state => {
      const weightsArr = scaleWeights(
        state.selfSelect.assets.map(x => x.weight),
        4
      )
      state.selfSelect.assets.map((x, i) => (x.weight = weightsArr[i]))
      state.selfSelect.portfolio.targetWeights.map((x, i) => (x.weight = weightsArr[i]))

      delete state.selfSelect.errors['total-weights']
      const weights = state.selfSelect.portfolio.targetWeights.map(x => x.weight)
      state.selfSelect.totalWeights = weights.reduce((sum, current) => sum + current, 0)
      if (state.selfSelect.totalWeights !== 1) {
        state.selfSelect.errors['total-weights'] = 'Weights must sum to 100%'
        state.selfSelect.areWeightsValid = false
      } else {
        state.selfSelect.areWeightsValid = true
      }

      const yieldWeight = state.selfSelect.portfolio.targetWeights
        .filter(x => x.asset.taxClassification === 'Yield')
        .map(x => x.weight)
        .reduce((sum, x) => sum + x, 0)
      state.selfSelect.taxClassification =
        yieldWeight >= 0.5 * state.selfSelect.totalWeights ? 'Yield' : 'Capital'

      if (state.note.taxClassification) {
        state.selfSelect.isTaxClassificationWarning =
          state.selfSelect.taxClassification !== state.note.taxClassification
      }
    },
    changeSelfSelectTransferUnits: (
      state,
      action: PayloadAction<{ assetId: string; units: number }>
    ) => {
      const units = truncate(action.payload.units, 6)
      if (
        state.selfSelect.transferUnits.filter(
          transfer => transfer.assetId === action.payload.assetId
        ).length > 0
      ) {
        const i = state.selfSelect.transferUnits.findIndex(
          transfer => transfer.assetId === action.payload.assetId
        )
        state.selfSelect.transferUnits[i].units = units
      } else {
        const transfer = {
          assetId: action.payload.assetId,
          units
        }
        state.selfSelect.transferUnits.push(transfer)
      }
      const j = state.selfSelect.assets.findIndex(x => x.id === action.payload.assetId)
      state.selfSelect.assets[j].units = units
      const totalPortfolioValue = getTotalPortfolioValueFromUnits(
        state.selfSelect.assets,
        state.assetPrices
      )
      state.selfSelect.totalPortfolioValue = totalPortfolioValue

      state.selfSelect.assets.forEach(asset => {
        const weight = (asset.units * state.assetPrices[asset.id]) / totalPortfolioValue
        asset.weight = weight
        const i = state.selfSelect.portfolio.targetWeights.findIndex(
          x => x.asset.assetId === asset.id
        )
        state.selfSelect.portfolio.targetWeights[i].weight = weight
      })

      delete state.selfSelect.errors['total-weights']
      state.selfSelect.totalWeights = 1
      state.selfSelect.areWeightsValid = true

      const yieldWeight = state.selfSelect.portfolio.targetWeights
        .filter(x => x.asset.taxClassification === 'Yield')
        .map(x => x.weight)
        .reduce((sum, x) => sum + x, 0)
      state.selfSelect.taxClassification =
        yieldWeight >= 0.5 * state.selfSelect.totalWeights ? 'Yield' : 'Capital'

      if (state.note.taxClassification) {
        state.selfSelect.isTaxClassificationWarning =
          state.selfSelect.taxClassification !== state.note.taxClassification
      }
    },
    setSelectedAssetDuration: (state, action: PayloadAction<string>) => {
      switch (state.portfolioType) {
        case 'ModelPortfolio':
          state.model.selectedAssetDuration = action.payload
          state.model.assets = state.model.allAssetPerformance[action.payload]
          break
        case 'FundOfFunds':
          state.model.selectedAssetDuration = action.payload
          state.model.assets = state.model.allAssetPerformance[action.payload]
          break
        case 'SelfSelect':
          state.selfSelect.selectedAssetDuration = action.payload
          state.selfSelect.assets = state.selfSelect.allAssetPerformance[action.payload]
      }
    },
    setDataIncomplete: state => {
      state.canProceed = false
    },
    setDataComplete: state => {
      state.canProceed = true
    },
    setBuilderErrors: (state, action: PayloadAction<Errors>) => {
      state.builderErrors = action.payload
    },
    stopChoosingPortfolio: state => {
      if (state.portfolioType === 'ModelPortfolio' || state.portfolioType === 'FundOfFunds') {
        state.model.isChoosing = false
      }
      if (state.portfolioType === 'SelfSelect') {
        state.selfSelect.isChoosingBasePortfolio = false
      }
      state.search.status = 'finding'
    },
    stopChoosingAsset: state => {
      state.selfSelect.isChoosingAsset = false
      state.searchAsset.status = 'finding'
    },
    changeSearchTerm: (state, action: PayloadAction<string>) => {
      state.search.searchTerm = action.payload
      if (action.payload === '') {
        state.search.filteredPortfolios = [...state.search.portfolios]
        return
      }
      state.search.filteredPortfolios = state.search.portfolios?.filter(
        x => x.name?.toLowerCase().indexOf(action.payload.toLowerCase()) !== -1
      )
    },
    changeAssetSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchAsset.searchTerm = action.payload
      if (action.payload === '') {
        state.searchAsset.filteredAssets = [...state.searchAsset.assets]
        return
      }
      state.searchAsset.filteredAssets = state.searchAsset.assets?.filter(
        x => x.name?.toLowerCase().indexOf(action.payload.toLowerCase()) !== -1
      )
    },
    goBackToSearch: state => {
      state.search.status = 'finding'
    },
    goBackToAssetSearch: state => {
      state.searchAsset.status = 'finding'
    },
    // payload = assetId
    inspectAsset: (state, action: PayloadAction<string>) => {
      state.isInspectingAsset = true
      if (state.portfolioType === 'ModelPortfolio' || state.portfolioType === 'FundOfFunds') {
        const assetFromModel = state.model?.portfolio?.targetWeights
          .flatMap(x => x.asset)
          .find(x => x.assetId === action.payload)
        state.assetBeingInspected = assetFromModel as any
      }
      if (state.portfolioType === 'SelfSelect') {
        const assetFromSelfSelect = state.selfSelect?.portfolio?.targetWeights
          .flatMap(x => x.asset)
          .find(x => x.assetId === action.payload)
        state.assetBeingInspected = assetFromSelfSelect as any
      }
    },
    stopInspectingAsset: state => {
      state.isInspectingAsset = false
      state.assetBeingInspected = undefined
    },
    updateAndMoveNext: () => {
      // dummy comment to keep linter happy, handled by builder saga
    },
    moveNext: () => {
      // dummy comment to keep linter happy, handled by builder saga
    },
    moveBack: () => {
      // dummy comment to keep linter happy, handled by builder saga
    }
  },
  extraReducers: builder => {
    builder
      .addCase('portfolioStep/inspectPortfolio', state => {
        state.search.status = 'inspecting'
      })
      .addCase('portfolioStep/inspectSelfSelectAsset', state => {
        state.searchAsset.status = 'inspecting'
      })

    // Apply logout extra reducers (handles both RESET and LOGOUT actions)
    const logoutReducers = createLogoutExtraReducers(initialState)
    Object.entries(logoutReducers).forEach(([actionType, reducer]) => {
      builder.addCase(actionType, reducer)
    })
  }
})

function* loadModelPortfolioStateSaga(
  portfolio: ApiModelPortfolio,
  disallowedStrategies: string[]
) {
  yield put(portfolioSlice.actions.setPortfolioTypeModel())
  yield put(
    portfolioSlice.actions.setModelPortfolio({
      model: portfolio,
      disallowedStrategiesFromState: disallowedStrategies
    })
  )
}

function* loadFofPortfolioStateSaga(
  portfolio: ApiModelPortfolio | ApiFundOfFundsPortfolio,
  disallowedStrategies: string[]
) {
  yield put(portfolioSlice.actions.setPortfolioTypeFundOfFund())
  yield put(
    portfolioSlice.actions.setModelPortfolio({
      model: portfolio as ApiModelPortfolio,
      disallowedStrategiesFromState: disallowedStrategies
    })
  )
}

function* loadSelfSelectPortfolioStateSaga(
  portfolio: ApiSelfSelectPortfolio,
  disallowedStrategies: string[],
  isTransfer: boolean,
  transferUnits: ApiTransferOfUnits[]
) {
  yield put(portfolioSlice.actions.setPortfolioTypeSelfSelect())
  yield put(
    portfolioSlice.actions.setSelfSelectPortfolio({
      portfolio,
      isTransfer,
      disallowedStrategiesFromState: disallowedStrategies,
      transferUnits
    })
  )
}

function* loadPendingOrCurrentStatePortfolioStateSaga(
  pendingAccountState: ApiAccountBuilderState | ApiAccountState,
  disallowedStrategiesFromState: string[],
  isTransfer: boolean,
  transferUnits: ApiTransferOfUnits[]
) {
  switch (pendingAccountState.portfolio.type) {
    case 'ModelPortfolio': {
      yield loadModelPortfolioStateSaga(
        pendingAccountState.portfolio as ApiModelPortfolio,
        disallowedStrategiesFromState
      )
      break
    }
    case 'FundOfFunds': {
      yield loadFofPortfolioStateSaga(
        pendingAccountState.portfolio as ApiFundOfFundsPortfolio,
        disallowedStrategiesFromState
      )
      break
    }
    case 'SelfSelect': {
      yield loadSelfSelectPortfolioStateSaga(
        pendingAccountState.portfolio as ApiSelfSelectPortfolio,
        disallowedStrategiesFromState,
        isTransfer,
        transferUnits
      )
      break
    }
    default: {
      throw new Error('Invariant violation: unsupported porfolio')
    }
  }
}

const loadState = (payload: SavedState) => ({ type: 'portfolioStep/loadState', payload })

function* loadStateSaga(action: PayloadAction<SavedState>) {
  const { account } = action.payload
  const { currentAccountState, newAccount, pendingAccountState } = account

  const isNewAccount = newAccount === true
  // 2337: for new accounts, we always use pending state
  let shouldUsePendingState = isNewAccount

  // If NOT a new account, and there is some saved pending portfolio state then use it
  if (!isNewAccount && pendingAccountState.portfolio) {
    shouldUsePendingState = true
  }

  yield put(portfolioSlice.actions.resetPortfolio())
  yield put(portfolioSlice.actions.init(action.payload.note))

  yield fetchPortfoliosAndStrategiesSaga()

  const portfolioDisallowedStrategies = shouldUsePendingState
    ? getDisallowedStrategiesFromPendingState(pendingAccountState as any)
    : getDisallowedStrategiesFromCurrentState(currentAccountState as any, account.businessUnit)

  const isTransfer = shouldUsePendingState ? pendingAccountState.transferUnits !== undefined : false
  const transferUnits = isTransfer ? pendingAccountState.transferUnits : null

  const currentOrPendingState = shouldUsePendingState ? pendingAccountState : currentAccountState
  if (!currentOrPendingState.portfolio) {
    return
  }

  yield loadPendingOrCurrentStatePortfolioStateSaga(
    currentOrPendingState as any,
    portfolioDisallowedStrategies,
    isTransfer,
    transferUnits
  )
  yield put(portfolioSlice.actions.setReadonlyCurrentOrPending(currentAccountState))
  yield put(portfolioSlice.actions.setReadonlyCurrentOrPending(pendingAccountState))
}

export const actions = {
  ...portfolioSlice.actions,
  inspectPortfolio: (id: string) => ({ type: 'portfolioStep/inspectPortfolio', payload: id }),
  inspectSelfSelectAsset: (id: string) => ({
    type: 'portfolioStep/inspectSelfSelectAsset',
    payload: id
  }),
  loadState
}

export type PortfolioStepActions = typeof actions

function* fetchPortfoliosSaga() {
  const state: RootState = yield select()
  const { businessUnitId } = state.builder.businessUnit
  try {
    const response = yield call(api.tenant.minimalDetail, businessUnitId, TENANT_ID)
    yield put({ type: 'portfolioStep/fetchPortfoliosSuccess', payload: response.data })
  } catch (error) {
    yield put({ type: 'portfolioStep/fetchPortfoliosFailure', payload: error })
  }
}

function* fetchPortfolioDetailSaga(action: PayloadAction<string>) {
  try {
    const response = yield call(api.tenant.portfolioDetail2, action.payload, TENANT_ID)
    yield put({ type: 'portfolioStep/fetchPortfolioDetailSuccess', payload: response.data })
  } catch (error) {
    yield put({ type: 'portfolioStep/fetchPortfolioDetailFailure', payload: error })
  }
}

function* fetchPortfolioPerformanceSaga(action: PayloadAction<ApiModelPortfolio>) {
  const portfolioInvestment = action.payload.targetWeights.map(targetWeight => ({
    assetId: targetWeight.asset.assetId,
    weight: targetWeight.weight
  }))

  const request = {
    start: null,
    end: null,
    portfolioInvestment
  }
  const backtestResult = yield call(api.tenant.portfolioBacktestCreate, TENANT_ID, request)
  const data = backtestResult.data as ApiBacktestResponse
  yield put({ type: 'portfolioStep/fetchAssetPerformancesSuccess', payload: data })
}

function* getInvestmentAmount() {
  const state: RootState = yield select()
  let investmentAmount = 1 // default to 1 if no investment value is available

  if (state.terms.terms.fundingSource === FundingSource.Switch) {
    if (state.terms.terms.fullSwitch && !state.terms.terms.switchTrade) {
      yield take('termsStep/setSwitchTrade')
      const trade = yield select((state: RootState) => state.terms.terms.switchTrade)
      investmentAmount = trade.accountValue
    } else {
      investmentAmount = state.terms.terms.fullSwitch
        ? state.terms.terms.switchTrade.accountValue
        : Number(state.terms.terms.switchInvestment)
    }
  } else if (state.terms.terms.investment && typeof state.terms.terms.investment === 'string') {
    investmentAmount = Number(state.terms.terms.investment)
  } else if (state.terms.terms.fundingSource === FundingSource.Transfer) {
    investmentAmount = state.portfolio.selfSelect?.totalPortfolioValue ?? 1
  }
  return investmentAmount
}

function* fetchPortfolioBacktestSaga(
  action: PayloadAction<{ disallowedStrategiesFromState: string[]; model?: ApiModelPortfolio }>
) {
  const investmentAmount = yield call(getInvestmentAmount)

  yield put({ type: 'portfolioStep/fetchingPortfolioBacktest' })

  const portfolioInvestment = action.payload.model.targetWeights.map(targetWeight => ({
    assetId: targetWeight.asset.assetId,
    weight: targetWeight.weight
  }))

  const request = {
    start: null,
    end: null,
    portfolioInvestment
  }

  try {
    const result = yield call(api.tenant.portfolioBacktestCreate, TENANT_ID, request)

    const data = result.data as ApiBacktestResponse
    const multiplied: Factor[] = []
    const timeseries = result.data.backtestTimeseries
    for (const date of Object.keys(timeseries)) {
      multiplied.push({
        timestamp: date,
        value: timeseries[date] * investmentAmount
      })
    }

    data.backtestTimeseries = multiplied as any

    yield put({ type: 'portfolioStep/fetchPortfolioBacktestSuccess', payload: data })
  } catch (error) {
    yield put({ type: 'portfolioStep/fetchPortfolioBacktestFailure', payload: error })
  }
}

function* fetchPortfolioProjectionSaga(
  action: PayloadAction<{ disallowedStrategiesFromState: string[]; model: ApiModelPortfolio }>
) {
  const investmentAmount = yield call(getInvestmentAmount)

  yield put({ type: 'portfolioStep/fetchingPortfolioProjection' })

  const portfolioInvestment = action.payload.model.targetWeights?.map(targetWeight => ({
    assetId: targetWeight.asset.assetId,
    weight: targetWeight.weight
  }))

  const request = {
    start: new Date().toJSON(),
    end: addYears(new Date(), 5).toJSON(),
    portfolioInvestment
  }

  try {
    const result = yield call(api.tenant.portfolioProjectionCreate, TENANT_ID, request)

    const data = result.data as ApiProjectionResponse
    const timeserieses = Object.values(data)
    const multiplied: Record<string, number>[] = []

    for (const timeseries of timeserieses) {
      const values: Record<string, number> = {}
      for (const date of Object.keys(timeseries)) {
        values[date] = timeseries[date] * investmentAmount
      }

      multiplied.push(values)
    }

    yield put({ type: 'portfolioStep/fetchPortfolioProjectionSuccess', payload: multiplied })
  } catch (error) {
    yield put({ type: 'portfolioStep/fetchPortfolioProjectionFailure', payload: error })
  }
}

function* fetchPerformanceAndProjectionSaga(
  action: PayloadAction<{ disallowedStrategiesFromState: string[]; model: ApiModelPortfolio }>
) {
  yield all([
    fetchPortfolioBacktestSaga(action),
    fetchPortfolioProjectionSaga(action),
    fetchNoteDataSaga(),
    fetchCalculatedPortfolioMultiplierSaga(action.payload.model)
  ])
}

function* fetchStrategiesSaga() {
  try {
    const response = yield call(api.tenant.strategyDetail, TENANT_ID)
    yield put({ type: 'portfolioStep/fetchStrategiesSuccess', payload: response.data })
  } catch (error) {
    yield put({ type: 'portfolioStep/fetchStrategiesFailure', payload: error })
  }
}

function* fetchPortfoliosAndStrategiesSaga() {
  yield all([fetchPortfoliosSaga(), fetchStrategiesSaga()])
}

function* fetchAssetsSaga() {
  try {
    const response = yield call(api.tenant.assetDetail, TENANT_ID)
    yield put({ type: 'portfolioStep/fetchAssetsSuccess', payload: response.data })
  } catch (error) {
    yield put({ type: 'portfolioStep/fetchAssetsFailure', payload: error })
  }
}

function* fetchAssetsAndStrategiesSaga() {
  yield all([fetchAssetsSaga(), fetchStrategiesSaga()])
}

function* fetchAssetDetailSaga(action: PayloadAction<string>) {
  try {
    const response = yield call(api.tenant.assetDetail2, action.payload, TENANT_ID)
    yield put({ type: 'portfolioStep/fetchAssetDetailSuccess', payload: response.data })
  } catch (error) {
    yield put({ type: 'portfolioStep/fetchAssetDetailFailure', payload: error })
  }
}

function* fetchSelfSelectPerformanceSaga(request) {
  const state: RootState = yield select()
  const investment = state.terms.terms.investment || state.portfolio.selfSelect?.totalPortfolioValue

  yield put({ type: 'portfolioStep/fetchingPortfolioBacktest' })
  try {
    const result = yield call(api.tenant.portfolioBacktestCreate, TENANT_ID, request)

    const data = result.data as ApiBacktestResponse
    const multiplied: Factor[] = []
    const timeseries = result.data.backtestTimeseries
    for (const date of Object.keys(timeseries)) {
      multiplied.push({
        timestamp: date,
        value: timeseries[date] * Number(investment)
      })
    }

    data.backtestTimeseries = multiplied as any

    yield put({ type: 'portfolioStep/fetchPortfolioBacktestSuccess', payload: data })
  } catch (error) {
    yield put({ type: 'portfolioStep/fetchPortfolioBacktestFailure', payload: error })
  }
}

function* fetchSelfSelectProjectionSaga(request) {
  const state: RootState = yield select()
  const investment = state.terms.terms.investment || state.portfolio.selfSelect?.totalPortfolioValue

  yield put({ type: 'portfolioStep/fetchingPortfolioProjection' })
  try {
    const result = yield call(api.tenant.portfolioProjectionCreate, TENANT_ID, request)

    const data = result.data as ApiProjectionResponse
    const timeserieses = Object.values(data)
    const multiplied: Record<string, number>[] = []

    for (const timeseries of timeserieses) {
      const values: Record<string, number> = {}
      for (const date of Object.keys(timeseries)) {
        values[date] = timeseries[date] * Number(investment)
      }

      multiplied.push(values)
    }

    yield put({ type: 'portfolioStep/fetchPortfolioProjectionSuccess', payload: multiplied })
  } catch (error) {
    yield put({ type: 'portfolioStep/fetchPortfolioProjectionFailure', payload: error })
  }
}

function* reloadPerformanceAndProjectionSaga() {
  const state: RootState = yield select()
  const { portfolio } = state

  if (!portfolio.portfolioType) {
    return
  }

  // validate before requesting charts
  const validationResult = validate(portfolio)

  if (!validationResult.isValid) {
    yield put(actions.setDataIncomplete())
    return
  }

  const portfolioInvestment = portfolio.selfSelect.portfolio.targetWeights.map(targetWeight => ({
    assetId: targetWeight.asset.assetId,
    weight: targetWeight.weight
  }))

  const backtestRequest = {
    start: null,
    end: null,
    portfolioInvestment
  }

  const projectionRequest = {
    start: new Date().toJSON(),
    end: addYears(new Date(), 5).toJSON(),
    portfolioInvestment
  }

  yield call(fetchLatestAssetPricesSaga)
  yield fork(fetchSelfSelectPerformanceSaga, backtestRequest)
  yield fork(fetchSelfSelectProjectionSaga, projectionRequest)
  yield fork(fetchNoteDataSaga)
  yield fork(fetchCalculatedPortfolioMultiplierSaga, portfolio.selfSelect.portfolio)
}

function* fetchNoteDataAndAssetPricesSaga() {
  yield all([fetchNoteDataSaga(), fetchLatestAssetPricesSaga()])
}

function* fetchLatestAssetPricesSaga() {
  try {
    const result = yield call(api.tenant.latestAssetspricesDetail, TENANT_ID)
    yield put({ type: 'portfolioStep/fetchAssetPricesSuccess', payload: result.data })
  } catch (error) {
    yield put({ type: 'portfolioStep/fetchAssetPricesFailure', payload: error })
  }
}

function* fetchCalculatedPortfolioMultiplierSaga(request) {
  try {
    const result = yield call(api.tenant.calculatemultiplierCreate, TENANT_ID, request)
    yield put({ type: 'portfolioStep/fetchCalculatedMultiplierSuccess', payload: result.data })
  } catch (error) {
    yield put({ type: 'portfolioStep/fetchCalculatedMultiplierFailure', payload: error })
  }
}

function* fetchNoteDataSaga() {
  const state: RootState = yield select()
  const { builder, portfolio, terms, topupWithdraw } = state
  const investment = yield call(getInvestmentAmount)

  const account = {
    accountId: builder.accountId,
    accountName: terms.terms.accountName,
    noteId: builder.note.noteId,
    investment,
    portfolioId:
      portfolio.portfolioType === 'ModelPortfolio' || portfolio.portfolioType === 'FundOfFunds'
        ? portfolio.model.portfolio.portfolioId
        : null,
    selfSelectPortfolio:
      portfolio.portfolioType === 'SelfSelect' ? portfolio.selfSelect.portfolio : null
  } as ApiUnapprovedAccount

  if (!builder.selectedAccount || builder.selectedAccount.newAccount) {
    try {
      const response = yield call(api.tenant.dataNewaccountCreate as any, TENANT_ID, account)
      yield put({ type: 'portfolioStep/fetchNoteDataSuccess', payload: response.data })
    } catch (error) {
      yield put({ type: 'portfolioStep/fetchNoteDataFailure', payload: error })
    }
  } else {
    const portfolioChangeInstruction = {
      accountId: builder.accountId,
      type: 'PortfolioChange' as const,
      valuationCycle: getDateOnlyAsJson(new Date().toDateString()),
      portfolioId:
        portfolio.portfolioType === 'ModelPortfolio' || portfolio.portfolioType === 'FundOfFunds'
          ? portfolio.model.portfolio.portfolioId
          : null,
      selfSelectPortfolio:
        portfolio.portfolioType === 'SelfSelect' ? portfolio.selfSelect.portfolio : null
    }

    const instructions = {
      portfolioChanges: [portfolioChangeInstruction],
      withdrawals: [],
      deposits: [],
      switchOutInstructions: []
    }

    if (topupWithdraw.topUpWithrawState === TopUpWithdrawState.Topup) {
      const depositInstruction = {
        accountId: builder.accountId,
        type: 'Deposit' as const,
        valuationCycle: getDateOnlyAsJson(new Date().toDateString()),
        requestedValue: Number(topupWithdraw.changeAmount)
      }
      instructions.deposits.push(depositInstruction)
    }
    if (topupWithdraw.topUpWithrawState === TopUpWithdrawState.Withdraw) {
      const withdrawalInstruction = {
        accountId: builder.accountId,
        type: 'Withdrawal' as const,
        valuationCycle: getDateOnlyAsJson(new Date().toDateString()),
        requestedValue: Number(topupWithdraw.changeAmount)
      }
      instructions.withdrawals.push(withdrawalInstruction)
    }
    try {
      const response = yield call(
        api.tenant.dataAccountNewinstructionsCreate,
        builder.accountId,
        TENANT_ID,
        instructions as any
      )
      yield put({ type: 'portfolioStep/fetchNoteDataSuccess', payload: response.data })
    } catch (error) {
      yield put({ type: 'portfolioStep/fetchNoteDataFailure', payload: error })
    }
  }
}

/**
 * This listens for certain actions, and if an action it is interested in does happen,
 * the performance and projection charts are refreshed.
 *
 * It also validates all the data and determines whether the user can proceed to the next
 * step or not.
 *
 * @param action potentially any action from any slice
 * @returns
 */
function* reloadChartsInResponseToActionsSaga(action) {
  if (
    action.type === 'portfolioStep/setSelfSelectPortfolio' ||
    action.type === 'portfolioStep/setAssetInSelfSelect' ||
    action.type === 'portfolioStep/removeAssetFromSelfSelect' ||
    action.type === 'portfolioStep/changeSelfSelectAssetWeight' ||
    action.type === 'portfolioStep/changeSelfSelectTransferUnits' ||
    action.type === 'portfolioStep/setSelfSelectRebalancingType' ||
    action.type === 'portfolioStep/setSelfSelectRebalancingFreq' ||
    action.type === 'portfolioStep/setSelfSelectRebalancingTolerance' ||
    action.type === 'portfolioStep/equaliseSelfSelectWeights' ||
    action.type === 'portfolioStep/scaleSelfSelectWeights'
  ) {
    yield reloadPerformanceAndProjectionSaga()
  }
}

export function* portfolioStepRootSaga() {
  yield all([
    takeLatest('portfolioStep/chooseModelPortfolio', fetchPortfoliosAndStrategiesSaga),
    takeLatest('portfolioStep/inspectPortfolio', fetchPortfolioDetailSaga),
    takeLatest('portfolioStep/inspectSelfSelectAsset', fetchAssetDetailSaga),
    takeLatest('portfolioStep/fetchPortfolioDetailSuccess', fetchPortfolioPerformanceSaga),
    takeLatest('portfolioStep/setModelPortfolio', fetchPerformanceAndProjectionSaga),
    takeLatest('portfolioStep/setSelfSelectPortfolio', reloadPerformanceAndProjectionSaga),
    takeLatest('portfolioStep/setBasePortfolio', fetchPerformanceAndProjectionSaga),
    takeLatest('portfolioStep/loadState', loadStateSaga),
    debounce(500, '*', reloadChartsInResponseToActionsSaga),
    takeLatest('portfolioStep/chooseAsset', fetchAssetsAndStrategiesSaga),
    takeEvery('builder/moveToPortfolio', fetchNoteDataAndAssetPricesSaga)
  ])
}

export default portfolioSlice.reducer
